import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Gear, Sliders } from 'react-bootstrap-icons';
import { AppStateContext } from '../../contexts/AppStateContext';
import { useTheme } from '../../contexts/ThemeContext';
import useIsMobile from '../../hooks/useIsMobile';
import BrokerNotConnectedState from '../../components/shared/BrokerNotConnectedState';
import PrimaryButton from '../../components/shared/PrimaryButton';
import ThemedSelect from '../../components/shared/ThemedSelect';
import FormInput from '../../components/shared/FormInput';
import TickerSearchInput from '../../components/shared/TickerSearchInput';
import BottomSheet from '../../components/shared/BottomSheet';
import FilterChip from '../../components/shared/FilterChip';
import FilterChipBar from '../../components/shared/FilterChipBar';
import SegmentedControl from '../../components/shared/SegmentedControl';
import DraggableSeparator from '../../components/shared/DraggableSeparator';
import { CHART_INTERVAL_OPTIONS } from '../../components/shared/ChartIntervalControl';
import { MOBILE_UI } from '../../constants/mobileUi';
import {
  LAYOUT_STORAGE_KEYS,
  readLayoutInt,
  writeLayoutInt,
} from '../../utils/layoutLocalStorage';
import { getStrategy, listStrategies } from '../strategies';
import { paramsFromSchema } from '../strategies/params';
import { loadWorkspaceState, saveWorkspaceState } from '../workspaceState';
import {
  BACKTEST_RANGE_PRESETS,
  clampRangeToLookback,
  defaultRangeForUnit,
  defaultRangePreset,
  isoToDateInput,
  rangeFromPreset,
} from '../engine/bars';
import { openPositionAtPlayhead, workingOrderAtPlayhead, skippedOrderAtPlayhead } from '../engine/replay';
import { useBacktestBars } from '../hooks/useBacktestBars';
import { useBacktestInstrument } from '../hooks/useBacktestInstrument';
import { useBacktestRun } from '../hooks/useBacktestRun';
import { useBarReplay } from '../hooks/useBarReplay';
import BacktestChart from './BacktestChart';
import { previewTradeFromWorkingOrder } from './backtestChartPrimitives';
import { chartOverlaySeries } from '../chartOverlays';
import { DEFAULT_TRADE_SESSION, normalizeTradeSession, tradeSessionBoxes } from '../engine/tradeSession';
import BacktestReplayBar from './BacktestReplayBar';
import BacktestStats from './BacktestStats';
import BacktestEquity from './BacktestEquity';
import BacktestTradesTable from './BacktestTradesTable';
import BacktestConfigModal from './BacktestConfigModal';
import StrategyParams from './StrategyParams';
import StrategyCriteria, { criteriaWithWorkingOrder } from './StrategyCriteria';
import { formatTradeTime } from './format';
import DashboardSectionTitle from '../../components/shared/DashboardSectionTitle';

// Pause after the last keystroke before recomputing, so typing a value doesn't rerun on every digit.
const INPUT_RERUN_DELAY_MS = 800;

const SESSION_OPTIONS = [
  { value: 'Default', label: 'RTH' },
  { value: 'USEQPreAndPost', label: 'Ext' },
];

const INTERVAL_OPTIONS = CHART_INTERVAL_OPTIONS.map((o) => ({
  value: `${o.interval}|${o.unit}`,
  label: o.display,
  interval: o.interval,
  unit: o.unit,
}));

function parseInterval(value) {
  const match = INTERVAL_OPTIONS.find((o) => o.value === value) || INTERVAL_OPTIONS[3];
  return { interval: match.interval, unit: match.unit, label: match.label, value: match.value };
}

export default function BacktestWorkspace({ embedded = false }) {
  const { colors } = useTheme();
  const isMobile = useIsMobile();
  const appState = useContext(AppStateContext) || {};
  const selectedTicker = appState.selectedTicker;
  const saved = useMemo(
    () => loadWorkspaceState({
      selectedTicker,
      intervalKeys: INTERVAL_OPTIONS.map((o) => o.value),
    }),
    // Hydrate once from localStorage; later edits persist via saveWorkspaceState.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const [strategyId, setStrategyId] = useState(saved.strategyId);
  const strategy = getStrategy(strategyId);
  const [params, setParams] = useState(saved.params);
  const [ticker, setTicker] = useState(saved.ticker);
  const [intervalKey, setIntervalKey] = useState(saved.intervalKey);
  const parsedTf = parseInterval(intervalKey);
  const [rangePreset, setRangePreset] = useState(saved.rangePreset);
  const [range, setRange] = useState(saved.range);
  const [sessionTemplate, setSessionTemplate] = useState(saved.sessionTemplate);
  const [positionMode, setPositionMode] = useState(saved.positionMode);
  const [sizeMode, setSizeMode] = useState(saved.sizeMode);
  const [fixedQty, setFixedQty] = useState(saved.fixedQty);
  const [maxRisk, setMaxRisk] = useState(saved.maxRisk);
  const [startingBalance, setStartingBalance] = useState(saved.startingBalance);
  const [riskPercent, setRiskPercent] = useState(saved.riskPercent);
  const [riskPercentBasis, setRiskPercentBasis] = useState(saved.riskPercentBasis);
  const [tradeSession, setTradeSession] = useState(saved.tradeSession);
  const [configOpen, setConfigOpen] = useState(false);
  const [selectedTradeId, setSelectedTradeId] = useState(null);
  const [mobilePane, setMobilePane] = useState('chart');
  const [inputsOpen, setInputsOpen] = useState(false);
  const [leftWidth, setLeftWidth] = useState(() => readLayoutInt(LAYOUT_STORAGE_KEYS.backtestLeftWidth, 248, { min: 200, max: 360 }));
  const [rightWidth, setRightWidth] = useState(() => readLayoutInt(LAYOUT_STORAGE_KEYS.backtestRightWidth, 268, { min: 220, max: 400 }));

  const strategies = listStrategies();
  const intervalLabel = parsedTf.label;

  const fetchRange = useMemo(
    () => clampRangeToLookback(range, parsedTf.interval, parsedTf.unit),
    [range, parsedTf.interval, parsedTf.unit],
  );

  const barsState = useBacktestBars({
    ticker,
    interval: parsedTf.interval,
    unit: parsedTf.unit,
    firstdate: fetchRange.firstdate,
    lastdate: fetchRange.lastdate,
    sessionTemplate,
    enabled: Boolean(ticker),
    extraTimeframes: strategy.requiredTimeframes || [],
  });
  const { pointValue, tickSize, ready: instrumentReady } = useBacktestInstrument(ticker);

  // Mirror the derived-default pattern: a strategy's `tickSize` paramSchema default is
  // normally a hardcoded guess, but when we know the real instrument tick size, use that as
  // the default instead. The user can still override it via the params UI (an explicit
  // params.tickSize always wins — see paramsFromSchema).
  const effectiveSchema = useMemo(() => {
    const schema = strategy.paramSchema;
    if (!schema?.some((f) => f.key === 'tickSize') || !Number.isFinite(tickSize) || tickSize <= 0) {
      return schema;
    }
    return schema.map((f) => (f.key === 'tickSize' ? { ...f, default: tickSize } : f));
  }, [strategy.paramSchema, tickSize]);

  const resolvedParams = useMemo(
    () => paramsFromSchema(effectiveSchema, params),
    [effectiveSchema, params],
  );
  const paramsKey = JSON.stringify(resolvedParams);
  const prevTypedInputKeyRef = useRef(null);
  const prevStructuralRef = useRef(null);
  const { result, running, error: runError, run, isStale } = useBacktestRun();
  const stale = isStale(
    strategy.id,
    resolvedParams,
    barsState.bars.length,
    positionMode,
    sizeMode,
    fixedQty,
    maxRisk,
    startingBalance,
    pointValue,
    riskPercent,
    riskPercentBasis,
    tradeSession,
  );
  const replay = useBarReplay(barsState.bars.length, { enabled: !isMobile || mobilePane === 'chart' });

  useEffect(() => {
    saveWorkspaceState({
      strategyId,
      ticker,
      intervalKey,
      rangePreset,
      range,
      sessionTemplate,
      params,
      positionMode,
      sizeMode,
      fixedQty,
      maxRisk,
      startingBalance,
      riskPercent,
      riskPercentBasis,
      tradeSession,
    });
  }, [strategyId, ticker, intervalKey, rangePreset, range, sessionTemplate, params, positionMode, sizeMode, fixedQty, maxRisk, startingBalance, riskPercent, riskPercentBasis, tradeSession]);

  useEffect(() => {
    if (selectedTicker && !ticker) setTicker(String(selectedTicker).toUpperCase());
  }, [selectedTicker, ticker]);

  useEffect(() => {
    if (!instrumentReady || !barsState.bars.length || barsState.loading) return undefined;
    const typedInputKey = `${paramsKey}\0${JSON.stringify(tradeSession)}`;
    const structural = [
      barsState.bars,
      barsState.timeframes,
      strategy.id,
      parsedTf.unit,
      positionMode,
      sizeMode,
      fixedQty,
      maxRisk,
      startingBalance,
      riskPercent,
      riskPercentBasis,
      pointValue,
    ];
    const structuralChanged = prevStructuralRef.current != null
      && structural.some((value, index) => value !== prevStructuralRef.current[index]);
    const typedChanged = prevTypedInputKeyRef.current != null && prevTypedInputKeyRef.current !== typedInputKey;
    prevTypedInputKeyRef.current = typedInputKey;
    prevStructuralRef.current = structural;
    const timer = window.setTimeout(() => {
      run({
        bars: barsState.bars,
        strategy,
        params: resolvedParams,
        unit: parsedTf.unit,
        positionMode,
        sizeMode,
        qty: fixedQty,
        maxRisk,
        startingBalance,
        riskPercent,
        riskPercentBasis,
        pointValue,
        tradeSession,
        timeframes: barsState.timeframes,
      });
      replay.toEnd();
    }, typedChanged && !structuralChanged ? INPUT_RERUN_DELAY_MS : 0);
    return () => window.clearTimeout(timer);
    // Auto-run when the loaded series, strategy, inputs, instrument, or committed position settings change.
    // Typed inputs wait so a few keystrokes collapse into one run. Series, strategy, and config save still run immediately.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [barsState.bars, barsState.loading, barsState.timeframes, strategy.id, paramsKey, parsedTf.unit, positionMode, sizeMode, fixedQty, maxRisk, startingBalance, riskPercent, riskPercentBasis, pointValue, instrumentReady, tradeSession]);

  const handleParam = useCallback((key, value) => {
    setParams((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleRun = useCallback(() => {
    run({
      bars: barsState.bars,
      strategy,
      params: resolvedParams,
      unit: parsedTf.unit,
      positionMode,
      sizeMode,
      qty: fixedQty,
      maxRisk,
      startingBalance,
      riskPercent,
      riskPercentBasis,
      pointValue,
      tradeSession,
      timeframes: barsState.timeframes,
    });
  }, [barsState.bars, barsState.timeframes, parsedTf.unit, pointValue, positionMode, resolvedParams, run, sizeMode, fixedQty, maxRisk, startingBalance, riskPercent, riskPercentBasis, strategy, tradeSession]);

  const handleSaveConfig = (next) => {
    setPositionMode(next.positionMode);
    setSizeMode(next.sizeMode);
    setFixedQty(next.fixedQty);
    setMaxRisk(next.maxRisk);
    setStartingBalance(next.startingBalance);
    setRiskPercent(next.riskPercent);
    setRiskPercentBasis(next.riskPercentBasis);
  };

  const handleStrategy = (id) => {
    setStrategyId(id);
    const next = getStrategy(id);
    const nextSchema = next.paramSchema?.some((f) => f.key === 'tickSize') && Number.isFinite(tickSize) && tickSize > 0
      ? next.paramSchema.map((f) => (f.key === 'tickSize' ? { ...f, default: tickSize } : f))
      : next.paramSchema;
    setParams(paramsFromSchema(nextSchema));
    setTradeSession(normalizeTradeSession(next.defaultTradeSession || DEFAULT_TRADE_SESSION));
  };

  const handleInterval = (value) => {
    const next = parseInterval(value);
    setIntervalKey(next.value);
    const preset = defaultRangePreset(next.unit);
    setRangePreset(preset);
    setRange(clampRangeToLookback(rangeFromPreset(preset), next.interval, next.unit));
  };

  const handleRangePreset = (id) => {
    setRangePreset(id);
    setRange(clampRangeToLookback(rangeFromPreset(id), parsedTf.interval, parsedTf.unit));
  };

  useEffect(() => {
    writeLayoutInt(LAYOUT_STORAGE_KEYS.backtestLeftWidth, leftWidth);
  }, [leftWidth]);
  useEffect(() => {
    writeLayoutInt(LAYOUT_STORAGE_KEYS.backtestRightWidth, rightWidth);
  }, [rightWidth]);

  useEffect(() => {
    if (isMobile) return undefined;
    const onKey = (event) => {
      const tag = event.target?.tagName;
      if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') return;
      if (event.code === 'Space') {
        event.preventDefault();
        replay.setPlaying((p) => !p);
      }
      if (event.key === 'ArrowLeft') replay.step(-1);
      if (event.key === 'ArrowRight') replay.step(1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isMobile, replay.setPlaying, replay.step]);

  const tableTrades = useMemo(
    () => result?.trades || [],
    [result],
  );
  const openPos = useMemo(
    () => (result ? openPositionAtPlayhead(result.trades, replay.playhead) : null),
    [result, replay.playhead],
  );
  const workingOrder = useMemo(
    () => previewTradeFromWorkingOrder(
      result ? workingOrderAtPlayhead(result.workingOrders, replay.playhead) : null,
    ),
    [result, replay.playhead],
  );
  const skippedOrder = useMemo(
    () => (result ? skippedOrderAtPlayhead(result.workingOrders, replay.playhead) : null),
    [result, replay.playhead],
  );
  const selectedTrade = useMemo(
    () => tableTrades.find((t) => t.id === selectedTradeId) || null,
    [tableTrades, selectedTradeId],
  );
  const overlays = useMemo(
    () => chartOverlaySeries(strategy, result?.indicators || {}, result?.params || params),
    [strategy, result?.indicators, result?.params, params],
  );
  const chartIndicators = useMemo(() => {
    const base = result?.indicators || {};
    const boxes = tradeSessionBoxes(barsState.bars, tradeSession);
    if (!boxes.length) return base;
    return {
      ...base,
      sessionBoxes: [...(base.sessionBoxes || []), ...boxes],
    };
  }, [result?.indicators, barsState.bars, tradeSession]);
  const criteria = useMemo(
    () => criteriaWithWorkingOrder(
      result?.barCriteria?.[replay.playhead] || [],
      workingOrder,
      skippedOrder,
    ),
    [result?.barCriteria, replay.playhead, workingOrder, skippedOrder],
  );
  const playBar = barsState.bars[replay.playhead];

  const onSelectTrade = (trade) => {
    setSelectedTradeId(trade.id);
    const idx = trade.exitIndex != null
      ? trade.exitIndex
      : Math.max(replay.playhead, trade.entryIndex ?? 0);
    replay.setPlayhead(idx);
    replay.setPlaying(false);
  };

  const chrome = (
    <BacktestToolbar
      colors={colors}
      isMobile={isMobile}
      strategyId={strategy.id}
      strategies={strategies}
      onStrategy={handleStrategy}
      ticker={ticker}
      onTicker={setTicker}
      intervalKey={parsedTf.value}
      onInterval={handleInterval}
      rangePreset={rangePreset}
      onRangePreset={handleRangePreset}
      range={range}
      onRange={(next) => setRange(clampRangeToLookback(
        typeof next === 'function' ? next(range) : next,
        parsedTf.interval,
        parsedTf.unit,
      ))}
      sessionTemplate={sessionTemplate}
      onSession={setSessionTemplate}
      spanLabel={barsState.spanLabel}
      loading={barsState.loading}
      truncated={barsState.truncated}
      progress={barsState.progress}
      stale={stale}
      running={running}
      onRun={handleRun}
      onConfig={() => setConfigOpen(true)}
      configOpen={configOpen}
    />
  );

  const configModal = (
    <BacktestConfigModal
      show={configOpen}
      onHide={() => setConfigOpen(false)}
      isMobile={isMobile}
      positionMode={positionMode}
      sizeMode={sizeMode}
      fixedQty={fixedQty}
      maxRisk={maxRisk}
      startingBalance={startingBalance}
      riskPercent={riskPercent}
      riskPercentBasis={riskPercentBasis}
      onSave={handleSaveConfig}
    />
  );

  const chartBlock = (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
        {barsState.waitingForBroker ? (
          <div style={centerNote(colors)}>Connecting broker…</div>
        ) : !barsState.canFetch ? (
          <BrokerNotConnectedState message="Connect a broker with market data to load history." />
        ) : barsState.loading ? (
          <div style={centerNote(colors)}>{barsState.progress ? `${barsState.progress.toLocaleString()} bars…` : 'Loading bars'}</div>
        ) : barsState.error ? (
          <div style={centerNote(colors)}>{barsState.error}</div>
        ) : (
          <BacktestChart
            bars={barsState.bars}
            playhead={replay.playhead}
            indicators={chartIndicators}
            overlays={overlays}
            trades={result?.trades || []}
            selectedTrade={selectedTrade}
            openPosition={openPos || workingOrder}
            colors={colors}
            onPlayhead={(idx) => {
              replay.setPlaying(false);
              replay.setPlayhead(idx);
            }}
          />
        )}
      </div>
      <BacktestReplayBar
        playhead={replay.playhead}
        barCount={barsState.bars.length}
        playing={replay.playing}
        speed={replay.speed}
        onPlayhead={replay.setPlayhead}
        onTogglePlay={() => replay.setPlaying((p) => !p)}
        onStep={replay.step}
        onSpeed={replay.setSpeed}
        isMobile={isMobile}
        stamp={playBar ? formatTradeTime(playBar.timestamp) : ''}
      />
      {runError ? (
        <div style={{ fontSize: 11, color: colors.danger || colors.textMuted, padding: isMobile ? '0 0 4px' : '0 0 2px' }}>
          {runError}
        </div>
      ) : null}
    </div>
  );

  const paramsBlock = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minHeight: 0 }}>
      <div>
        <DashboardSectionTitle title="Inputs" marginBottom={8} as="h3" />
        <StrategyParams
          schema={effectiveSchema}
          values={params}
          onChange={handleParam}
          compact={isMobile}
          session={tradeSession}
          onSessionChange={setTradeSession}
        />
      </div>
      <div>
        <DashboardSectionTitle title="Strategy conditions" marginBottom={8} as="h3" />
        <StrategyCriteria items={criteria} />
      </div>
    </div>
  );

  const resultsBlock = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <BacktestStats stats={result?.stats} stale={stale} />
      <BacktestEquity equity={result?.equity || []} stale={stale} />
      <BacktestTradesTable
        trades={tableTrades}
        selectedId={selectedTradeId}
        onSelect={onSelectTrade}
        stale={stale}
      />
    </div>
  );

  if (isMobile) {
    return (
      <div data-testid="backtest-workspace" style={shellStyle(colors, embedded, true)}>
        <FilterChipBar bleed={embedded ? MOBILE_UI.pagePadding : 0} ariaLabel="Backtest controls">
          <FilterChip value={ticker} onClick={() => setInputsOpen(true)} />
          <FilterChip value={parsedTf.label} onClick={() => setInputsOpen(true)} />
          <FilterChip value="Inputs" leadingIcon={<Sliders size={12} />} onClick={() => setInputsOpen(true)} />
          <FilterChip value="Config" leadingIcon={<Gear size={12} />} onClick={() => setConfigOpen(true)} />
          <FilterChip
            value={stale ? 'Run' : 'Ran'}
            active={stale}
            trailing="none"
            onClick={handleRun}
          />
        </FilterChipBar>
        <div style={{ padding: `8px 0 10px` }}>
          <SegmentedControl
            size="sm"
            variant="accent"
            ariaLabel="Backtest pane"
            value={mobilePane}
            onChange={setMobilePane}
            options={[
              { value: 'chart', label: 'Chart' },
              { value: 'trades', label: 'Trades' },
              { value: 'stats', label: 'Stats' },
            ]}
          />
        </div>
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: mobilePane === 'chart' ? 'hidden' : 'auto' }}>
          {mobilePane === 'chart' && chartBlock}
          {mobilePane === 'trades' && resultsBlock}
          {mobilePane === 'stats' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <BacktestStats stats={result?.stats} stale={stale} />
              <BacktestEquity equity={result?.equity || []} stale={stale} />
            </div>
          )}
        </div>
        <BottomSheet show={inputsOpen} onHide={() => setInputsOpen(false)} title="Inputs">
          {chrome}
          <div style={{ marginTop: 14 }}>{paramsBlock}</div>
        </BottomSheet>
        {configModal}
      </div>
    );
  }

  return (
    <div data-testid="backtest-workspace" style={shellStyle(colors, embedded, false)}>
      {chrome}
      <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
        <aside style={{ width: leftWidth, flexShrink: 0, overflow: 'auto', padding: '12px 12px 16px', borderRight: `1px solid ${colors.border}` }}>
          {paramsBlock}
        </aside>
        <DraggableSeparator
          side="left"
          minWidth={200}
          maxWidth={360}
          onResize={setLeftWidth}
        />
        <main style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', padding: '8px 10px 10px' }}>
          {chartBlock}
        </main>
        <DraggableSeparator
          side="right"
          minWidth={220}
          maxWidth={400}
          onResize={setRightWidth}
        />
        <aside style={{ width: rightWidth, flexShrink: 0, overflow: 'auto', padding: '12px 12px 16px', borderLeft: `1px solid ${colors.border}` }}>
          {resultsBlock}
        </aside>
      </div>
      {configModal}
    </div>
  );
}

function BacktestToolbar({
  colors,
  isMobile,
  strategyId,
  strategies,
  onStrategy,
  ticker,
  onTicker,
  intervalKey,
  onInterval,
  rangePreset,
  onRangePreset,
  range,
  onRange,
  sessionTemplate,
  onSession,
  spanLabel,
  loading,
  truncated,
  progress,
  stale,
  running,
  onRun,
  onConfig,
  configOpen,
}) {
  const controlH = isMobile ? MOBILE_UI.controlHeight : 32;
  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'flex-end',
        gap: 8,
        padding: isMobile ? '0 0 8px' : '10px 12px',
        borderBottom: isMobile ? 'none' : `1px solid ${colors.border}`,
        flexShrink: 0,
        position: 'relative',
        zIndex: 8,
      }}
    >
      <ToolbarField colors={colors} label="Strategy">
        <ThemedSelect
          aria-label="Strategy"
          value={strategyId}
          options={strategies.map((s) => ({ value: s.id, label: s.name }))}
          onChange={onStrategy}
          width={140}
          height={controlH}
        />
      </ToolbarField>
      <ToolbarField colors={colors} label="Symbol">
        <TickerSearchInput
          value={ticker}
          onChange={onTicker}
          placeholder="MES"
          ariaLabel="Symbol"
          width={110}
          height={controlH}
        />
      </ToolbarField>
      <ToolbarField colors={colors} label="Timeframe">
        <ThemedSelect
          aria-label="Timeframe"
          value={intervalKey}
          options={INTERVAL_OPTIONS}
          onChange={onInterval}
          width={96}
          height={controlH}
        />
      </ToolbarField>
      <ToolbarField colors={colors} label="Range">
        <ThemedSelect
          aria-label="Backtest range"
          value={rangePreset}
          options={BACKTEST_RANGE_PRESETS.map((p) => ({ value: p.id, label: p.label }))}
          onChange={onRangePreset}
          width={72}
          height={controlH}
        />
      </ToolbarField>
      <ToolbarField colors={colors} label="Session">
        <ThemedSelect
          aria-label="Session"
          value={sessionTemplate}
          options={SESSION_OPTIONS}
          onChange={onSession}
          width={72}
          height={controlH}
        />
      </ToolbarField>
      <ToolbarField colors={colors} label="From">
        <FormInput
          type="date"
          value={range.firstdate}
          onChange={(e) => onRange((prev) => ({ ...prev, firstdate: isoToDateInput(e.target.value) }))}
          style={{ width: 132, height: controlH }}
        />
      </ToolbarField>
      <ToolbarField colors={colors} label="To">
        <FormInput
          type="date"
          value={range.lastdate}
          onChange={(e) => onRange((prev) => ({ ...prev, lastdate: isoToDateInput(e.target.value) }))}
          style={{ width: 132, height: controlH }}
        />
      </ToolbarField>
      <span
        data-testid="backtest-span-label"
        style={{
          fontSize: 12,
          color: colors.textMuted,
          fontVariantNumeric: 'tabular-nums',
          flex: 1,
          minWidth: 140,
          paddingBottom: 8,
          lineHeight: 1.2,
        }}
      >
        {loading ? (progress ? `${progress.toLocaleString()} bars…` : 'Loading…') : spanLabel}
        {truncated ? ' · capped' : ''}
      </span>
      <button
        type="button"
        aria-label="Config"
        aria-expanded={configOpen}
        onClick={onConfig}
        style={{
          height: controlH,
          padding: '0 10px',
          borderRadius: 8,
          border: `1px solid ${configOpen ? colors.primary : colors.border}`,
          background: configOpen ? `${colors.primary}22` : 'transparent',
          color: configOpen ? colors.primary : colors.textMuted,
          fontSize: 12,
          fontWeight: 600,
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        <Gear size={14} />
        Config
      </button>
      <PrimaryButton size="sm" variant="compact" loading={running} onClick={onRun} style={stale ? { boxShadow: `0 0 0 1px ${colors.primary}` } : undefined}>
        Run
      </PrimaryButton>
      {loading ? (
        <div
          aria-hidden
          style={{
            flexBasis: '100%',
            height: 2,
            borderRadius: 99,
            background: colors.border,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: progress ? `${Math.min(100, (progress / 8000) * 100)}%` : '18%',
              height: '100%',
              background: colors.primary,
              transition: 'width 160ms linear',
            }}
          />
        </div>
      ) : null}
    </div>
  );
}

function ToolbarField({ colors, label, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
      <span
        style={{
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          color: colors.textMuted,
        }}
      >
        {label}
      </span>
      {children}
    </label>
  );
}

function shellStyle(colors, embedded, isMobile) {
  return {
    height: embedded || isMobile ? '100%' : '100%',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    background: colors.bgPrimary,
    color: colors.text,
    overflow: 'hidden',
  };
}

function centerNote(colors) {
  return {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: colors.textMuted,
    fontSize: 13,
  };
}
